/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { DateTime } from "luxon";
import "react-datepicker/dist/react-datepicker.css";
import DeviceAnalyticTimeFilterPanel from "./DeviceAnalyticTimeFilterPanel.js";
import logStore from "../../../../stores/LogStore.js";
import deviceEventStore from "../../../../stores/DeviceEventStore";
import {
  ChartBlock,
  ValveStateCard,
  ValveCountCard,
} from "./AnalyticChartBlock.js";
import { filterSingleDeviceData } from "../../../../utils/logDataFilter&Cleaner.js";

const DeviceAnalytic = observer(() => {
  const { pimios_id } = useParams();
  const deviceId = pimios_id?.split("-")[0];

  const currentMonth = deviceEventStore.getMonthKey(new Date());

  const valve1Events =
    deviceEventStore.getValveEvents(deviceId, currentMonth)?.v1 || [];
  const valve2Events =
    deviceEventStore.getValveEvents(deviceId, currentMonth)?.v2 || [];

  const valve1OpenCount = valve1Events.filter(
    (e) => e.payload?.state?.toLowerCase() === "opened"
  ).length;
  const valve1CloseCount = valve1Events.filter(
    (e) => e.payload?.state?.toLowerCase() === "closed"
  ).length;

  const valve2OpenCount = valve2Events.filter(
    (e) => e.payload?.state?.toLowerCase() === "open"
  ).length;
  const valve2CloseCount = valve2Events.filter(
    (e) => e.payload?.state?.toLowerCase() === "closed"
  ).length;

  const [valve1DataArr, setValve1DataArr] = useState([]);
  const [valve2DataArr, setValve2DataArr] = useState([]);
  const [visibleSeries, setVisibleSeries] = useState({});
  const [latestLogTime, setLatestLogTime] = useState("");

  const [v1ValveState, setV1ValveState] = useState(0);
  const [v2ValveState, setV2ValveState] = useState(0);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [compareMethod, setCompareMethod] = useState("closest");

  const toggleSeriesVisibility = (key) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // const requestLogs = useCallback(() => {
  //   logStore.requestLogs();
  // }, []);

  const handleApplyFilter = () => {
    if (!startDate || !endDate) return;
    const start = DateTime.fromJSDate(startDate);
    const end = DateTime.fromJSDate(endDate);

    const deviceLogIds = logStore.deviceLogIds.get(deviceId) || new Set();
    const logs = Array.from(deviceLogIds)
      .map((id) => logStore.logsMap.get(id))
      .filter((log) => !!log)
      .sort((a, b) => {
        const t1 = DateTime.fromFormat(a.user_local_t, "M/d/yyyy, h:mm:ss a");
        const t2 = DateTime.fromFormat(b.user_local_t, "M/d/yyyy, h:mm:ss a");
        return t1.toMillis() - t2.toMillis();
      });

    const inRange = logs.filter((log) => {
      const dt = DateTime.fromFormat(log.user_local_t, "M/d/yyyy, h:mm:ss a");
      return dt >= start && dt <= end;
    });

    let v1List = [],
      v2List = [];

    if (compareMethod === "closest") {
      const getClosest = (target) => {
        if (inRange.length === 0) return null;
        return inRange.reduce((prev, curr) => {
          const prevTime = DateTime.fromFormat(
            prev.user_local_t,
            "M/d/yyyy, h:mm:ss a"
          );
          const currTime = DateTime.fromFormat(
            curr.user_local_t,
            "M/d/yyyy, h:mm:ss a"
          );
          return Math.abs(currTime - target) < Math.abs(prevTime - target)
            ? curr
            : prev;
        });
      };

      [getClosest(start), getClosest(end)].forEach((log) => {
        if (log) {
          const { valve1Data, valve2Data } = filterSingleDeviceData(log);
          if (valve1Data)
            v1List.push({ ...valve1Data, timestamp: log.user_local_t });
          if (valve2Data)
            v2List.push({ ...valve2Data, timestamp: log.user_local_t });
        }
      });
    } else {
      const dataPoints = inRange.map((log) => {
        const { valve1Data, valve2Data } = filterSingleDeviceData(log);
        return { time: log.user_local_t, v1: valve1Data, v2: valve2Data };
      });

      const reduceMetrics = (items, method) => {
        if (!items.length) return {};

        const keys = Object.keys(items[0]);

        const reducer = (key) => {
          const values = items
            .map((i) => i[key])
            .filter((v) => typeof v === "number");

          if (!values.length) return null;

          switch (method) {
            case "average":
              return values.reduce((a, b) => a + b, 0) / values.length;

            case "median": {
              const sorted = [...values].sort((a, b) => a - b);
              const mid = Math.floor(sorted.length / 2);
              return sorted.length % 2 === 0
                ? (sorted[mid - 1] + sorted[mid]) / 2
                : sorted[mid];
            }

            case "min":
              return Math.min(...values);

            case "max":
              return Math.max(...values);

            case "diff":
              return values.length >= 2 ? values.at(-1) - values[0] : 0;

            default:
              return null;
          }
        };

        const result = {};
        keys.forEach((key) => {
          result[key] = reducer(key);
        });

        return result;
      };

      const safeAggregatedPoint = (aggregated, label) =>
        Object.keys(aggregated).length > 0
          ? [{ ...aggregated, timestamp: label }]
          : [];

      const v1Items = dataPoints.map((d) => d.v1).filter(Boolean);
      const v2Items = dataPoints.map((d) => d.v2).filter(Boolean);

      // console.log(`--- Applying Compare Method: ${compareMethod} ---`);
      // console.log("Valve 1 Raw Data Points:", v1Items);
      // console.log("Valve 2 Raw Data Points:", v2Items);

      const v1Aggregated = reduceMetrics(v1Items, compareMethod);
      const v2Aggregated = reduceMetrics(v2Items, compareMethod);

      // console.log("Valve 1 Aggregated Result:", v1Aggregated);
      // console.log("Valve 2 Aggregated Result:", v2Aggregated);

      const timestampLabel = `${start.toLocaleString(DateTime.DATETIME_MED)} → ${end.toLocaleString(DateTime.DATETIME_MED)}`;

      v1List = safeAggregatedPoint(v1Aggregated, timestampLabel);
      v2List = safeAggregatedPoint(v2Aggregated, timestampLabel);
    }

    setValve1DataArr(v1List);
    setValve2DataArr(v2List);
    setIsFilterActive(true);
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (isFilterActive) return;

    const interval = setInterval(() => {
      const deviceLogIds = logStore.deviceLogIds.get(deviceId) || new Set();
      const filtered = Array.from(deviceLogIds)
        .map((id) => logStore.logsMap.get(id))
        .filter((log) => !!log)
        .sort((a, b) => {
          const t1 = DateTime.fromFormat(a.user_local_t, "M/d/yyyy, h:mm:ss a");
          const t2 = DateTime.fromFormat(b.user_local_t, "M/d/yyyy, h:mm:ss a");
          return t1.toMillis() - t2.toMillis();
        });

      const latest = filtered.at(-1);

      if (latest?.user_local_t) {
        setLatestLogTime(latest.user_local_t);

        if (latest?.v1_valve_state !== undefined) {
          setV1ValveState(latest.v1_valve_state);
        }

        if (latest?.V2pos1 !== undefined && latest?.V2pos2 !== undefined) {
          if (latest.V2pos1 === 1 && latest.V2pos2 === 0) {
            setV2ValveState(1); // Open
          } else if (latest.V2pos1 === 0 && latest.V2pos2 === 1) {
            setV2ValveState(0); // Closed
          }
        }
      }

      const v1List = [];
      const v2List = [];

      filtered.forEach((log) => {
        const { valve1Data, valve2Data } = filterSingleDeviceData(log);
        if (valve1Data) {
          v1List.push({ ...valve1Data, timestamp: log.user_local_t });
        }
        if (valve2Data) {
          v2List.push({ ...valve2Data, timestamp: log.user_local_t });
        }
      });

      setValve1DataArr(v1List.slice(-50));
      setValve2DataArr(v2List.slice(-50));
    }, 1000);

    return () => clearInterval(interval);
  }, [deviceId, isFilterActive]);

  useEffect(() => {
    if (!isFilterActive) return;

    const timeout = setTimeout(() => {
      handleApplyFilter();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [logStore.currentLogs.length, startDate, endDate, compareMethod]);

  const EXCLUDED_KEYS = [
    "v1_stat_1",
    "v1_stat_2",
    "v2_stat_1",
    "v2_stat_2",
    "v1_valve_state",
  ];

  const extractKeys = (dataArr) =>
    dataArr.length > 0
      ? Object.keys(dataArr[0]).filter(
          (k) =>
            k !== "timestamp" &&
            typeof dataArr[0][k] === "number" &&
            !EXCLUDED_KEYS.includes(k)
        )
      : [];

  const valve1Keys = extractKeys(valve1DataArr);
  const valve2Keys = extractKeys(valve2DataArr);

  useEffect(() => {
    valve1Keys.forEach((key) => {
      setVisibleSeries((prev) => {
        if (!(key in prev)) {
          return { ...prev, [key]: true };
        }
        return prev;
      });
    });

    valve2Keys.forEach((key) => {
      setVisibleSeries((prev) => {
        if (!(key in prev)) {
          return { ...prev, [key]: true };
        }
        return prev;
      });
    });
  }, [valve1Keys, valve2Keys]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Banner Card with title and Device ID */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Device Analytics - Latest Log at {latestLogTime}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            WebSocket: {logStore.connectionStatus}
          </Typography>
        </CardContent>
      </Card>
      <Box display="flex" gap={2} mb={2}>
        <Button
          variant="contained"
          // onClick={requestLogs}
          disabled={!logStore.isConnected}
          sx={{
            textTransform: "none",
            borderRadius: 1.5,
            backgroundColor: "#22C55E",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#16a34a", // slightly darker green for hover
            },
          }}
        >
          Refresh Data
        </Button>
        <Button
          variant="outlined"
          onClick={() => setIsDrawerOpen(true)}
          sx={{
            height: 36,
            px: 2,
            textTransform: "none",
            borderRadius: 2,
            borderColor: "#22C55E",
            backgroundColor: "transparent",
            color: "#22C55E",
            alignSelf: "flex-end",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          }}
        >
          More Filters
        </Button>
      </Box>
      <DeviceAnalyticTimeFilterPanel
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fromDate={startDate}
        toDate={endDate}
        setFromDate={setStartDate}
        setToDate={setEndDate}
        onApply={handleApplyFilter}
        onClear={() => {
          setIsFilterActive(false);
          setStartDate(null);
          setEndDate(null);
          setCompareMethod("closest");
        }}
        compareMethod={compareMethod}
        setCompareMethod={setCompareMethod}
      />
      {/* <ChartBlock
        title="Valve 1 Analytics"
        data={valve1DataArr}
        keys={valve1Keys}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeriesVisibility}
        extra={<ValveStateCard state={v1ValveState} />}
        connectionStatus={logStore.connectionStatus}
        useBarChart={isFilterActive}
        isFilterActive={isFilterActive}
      />
      <ChartBlock
        title="Valve 2 Analytics"
        data={valve2DataArr}
        keys={valve2Keys}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeriesVisibility}
        connectionStatus={logStore.connectionStatus}
        useBarChart={isFilterActive}
        isFilterActive={isFilterActive}
      /> */}

      <ChartBlock
        title="Valve 1 Analytics"
        data={valve1DataArr}
        keys={valve1Keys}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeriesVisibility}
        extra={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ValveStateCard state={v1ValveState} />
            <ValveCountCard
              openCount={valve1OpenCount}
              closeCount={valve1CloseCount}
            />
          </Box>
        }
        connectionStatus={logStore.connectionStatus}
        useBarChart={isFilterActive}
        isFilterActive={isFilterActive}
      />

      <ChartBlock
        title="Valve 2 Analytics"
        data={valve2DataArr}
        keys={valve2Keys}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeriesVisibility}
        extra={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ValveStateCard state={v2ValveState} />
            <ValveCountCard
              openCount={valve2OpenCount}
              closeCount={valve2CloseCount}
            />
          </Box>
        }
        connectionStatus={logStore.connectionStatus}
        useBarChart={isFilterActive}
        isFilterActive={isFilterActive}
      />
    </Box>
  );
});

export default DeviceAnalytic;
